import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';
import { MLML_LEAD_COOKIE_NAME } from 'constants/cookies';
import { parseAffiliateHandle } from 'utils/my-link-my-lead/affiliate-handle-processor';

// Paths where injecting a handle would be harmful or meaningless.
const EXCLUDED_PREFIXES = ['/api/', '/_next/', '/widget/'];

function isExcludedPath(path: string): boolean {
  return EXCLUDED_PREFIXES.some(prefix => path.startsWith(prefix));
}

function getHandleFromCookie(): string | null {
  try {
    const raw = Cookies.get(MLML_LEAD_COOKIE_NAME);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data.handle ?? null;
  } catch {
    return null;
  }
}

/**
 * Keeps the agent handle visible in the browser address bar across all navigation types.
 *
 * The middleware strips the handle before routing so pages never receive it as part of
 * their URL — but we still want the visitor to see `/@handle` in the bar at all times
 * (for sharing, bookmarking, and attribution continuity). This hook re-appends the handle
 * via three complementary triggers:
 *
 *   1. `history.replaceState` patch — catches filter/sort changes on the hybrid search page
 *      that update the URL without a full navigation.
 *   2. `routeChangeComplete` — catches Next.js soft navigations (router.push, <Link>).
 *   3. Mount effect — ensures the handle is present on the initial page load.
 */
export function useHandleInjection(): void {
  const router = useRouter();
  // Ref to the original replaceState — populated before the patch is installed so
  // the patched version can call through without recursing into itself.
  const originalReplaceState = useRef<typeof window.history.replaceState | null>(null);

  const injectHandle = useCallback(() => {
    const handle = getHandleFromCookie();
    if (!handle) return;

    const currentPath = window.location.pathname;
    if (isExcludedPath(currentPath)) return;
    // Skip if the handle is already present — parseAffiliateHandle returns non-null when it is.
    if (parseAffiliateHandle(currentPath)) return;

    // Normalise root: "/" → "/@handle", "/path" → "/path/@handle"
    const basePath = currentPath === '/' ? '' : currentPath;
    const newPath = `${basePath}/@${handle}`;
    const fullUrl = `${newPath}${window.location.search}${window.location.hash}`;

    // Use the captured original to avoid re-triggering the patch below.
    const native = originalReplaceState.current ?? window.history.replaceState;
    native.call(window.history, window.history.state, '', fullUrl);
  }, []);

  // Patch history.replaceState once so any caller (hybrid page filters, etc.)
  // automatically gets the handle re-appended — no call site needs to know about handles.
  useEffect(() => {
    const original = window.history.replaceState.bind(window.history);
    originalReplaceState.current = original;

    window.history.replaceState = function patchedReplaceState(state, title, url) {
      original(state, title, url);
      // Defer injection so it runs after the caller's URL has fully settled.
      setTimeout(injectHandle, 0);
    };

    return () => {
      window.history.replaceState = original;
      originalReplaceState.current = null;
    };
  }, [injectHandle]);

  // Cover Next.js soft navigations (router.push / Link clicks).
  useEffect(() => {
    router.events.on('routeChangeComplete', injectHandle);
    return () => {
      router.events.off('routeChangeComplete', injectHandle);
    };
  }, [router.events, injectHandle]);

  // Inject on initial mount in case the page loaded without a handle in the URL.
  useEffect(() => {
    injectHandle();
  }, [injectHandle]);
}
